import { Injectable } from '@angular/core';
import { GridSettings } from '../classes/grid-settings.model';
import { GridColumn } from '../classes/column.interface';
import { BehaviorSubject, Observable, map } from 'rxjs';
//import { FilterDescriptor, ComplexFilterDescriptor } from '../utils/filter.util';
import { 
  filterBy, 
  //normalizeFilters,
  createFieldFilter,
  createDateRangeFilter,
  createNumericRangeFilter,
  createMultiValueFilter,
  createTextSearchFilter,
  combineFiltersWithAnd,
  combineFiltersWithOr,
  describeFilter,
  isValidFilterExpression,
  FilterDescriptor,
  ComplexFilterDescriptor,
  FilterExpression,
} from '../utils/filter.util';

/**
 * Interface for filter state management
 */
export interface FilterState {
  readonly columnFilters: ReadonlyMap<string, FilterDescriptor>;
  readonly globalFilter: string;
  readonly complexFilter?: ComplexFilterDescriptor;
  readonly isActive: boolean;
}

/**
 * Interface for filter change events
 */
export interface FilterChangeEvent {
  readonly field: string;
  readonly value: unknown;
  readonly operator?: string;
  readonly previousValue?: unknown;
}

/**
 * Interface for filter presets
 */
export interface FilterPreset {
  readonly name: string;
  readonly description: string;
  readonly filterFactory: (field: string) => FilterExpression;
}

/**
 * Type guard for checking if a filter preset is valid
 */
function isValidFilterPreset(preset: unknown): preset is FilterPreset {
  return typeof preset === 'object' && 
         preset !== null &&
         'name' in preset &&
         'description' in preset &&
         'filterFactory' in preset &&
         typeof (preset as FilterPreset).name === 'string' &&
         typeof (preset as FilterPreset).description === 'string' &&
         typeof (preset as FilterPreset).filterFactory === 'function';
}

/**
 * Service for managing grid filters with reactive state
 */
@Injectable({
    providedIn: 'root',
})
export class TableFilterService {
    private readonly filterStateSubject = new BehaviorSubject<FilterState>({
    columnFilters: new Map(),
    globalFilter: '',
    isActive: false,
  });

  private readonly filterPresets = new Map<string, FilterPreset>();

  // Public observables
  public readonly filterState$: Observable<FilterState> = this.filterStateSubject.asObservable();
  
  public readonly hasActiveFilters$: Observable<boolean> = this.filterState$.pipe(
    map((state: FilterState) => state.isActive || 
                                state.globalFilter.trim() !== '' || 
                                state.columnFilters.size > 0),
  );

    constructor() {
    this.initializeDefaultPresets();
  }

  /**
   * Get the current filter state
   */
  public getCurrentFilterState(): FilterState {
    return this.filterStateSubject.value;
  }

  /**
   * Update the filter state with partial updates
   */
  private updateFilterState(updates: Partial<FilterState>): void {
    const currentState = this.getCurrentFilterState();
    const newState: FilterState = {
      ...currentState,
      ...updates,
      isActive: this.determineIfFiltersActive({
        ...currentState,
        ...updates,
      }),
    };
    this.filterStateSubject.next(newState);
  }

  /**
   * Determine if any filters are currently active
   */
  private determineIfFiltersActive(state: Partial<FilterState>): boolean {
    const globalFilter = state.globalFilter ?? '';
    const columnFilters = state.columnFilters ?? new Map();
    const complexFilter = state.complexFilter;
    
    return globalFilter.trim() !== '' || 
           columnFilters.size > 0 || 
           (complexFilter != null && complexFilter.filters.length > 0);
  }

  /**
   * Set a filter for a specific column
   */
  public setColumnFilter(field: string, value: unknown, operator = 'contains', ignoreCase = true): void {
    if (!field || field.trim() === '') {
      console.warn('Field name cannot be empty');
      return;
    }

    const currentState = this.getCurrentFilterState();
    const newFilters = new Map(currentState.columnFilters);

    if (value === null || value === undefined || value === '') {
      newFilters.delete(field);
    } else {
      const filter = createFieldFilter(field, value, operator, ignoreCase);
      newFilters.set(field, filter);
    }

    this.updateFilterState({ columnFilters: newFilters });
  }

  /**
   * Get a filter for a specific column
   */
  public getColumnFilter(field: string): FilterDescriptor | undefined {
    return this.getCurrentFilterState().columnFilters.get(field);
  }

  /**
   * Remove a filter for a specific column
   */
  public removeColumnFilter(field: string): void {
    const currentState = this.getCurrentFilterState();
    const newFilters = new Map(currentState.columnFilters);
    const wasRemoved = newFilters.delete(field);

    if (wasRemoved) {
      this.updateFilterState({ columnFilters: newFilters });
    }
  }

  /**
   * Clear all column filters
   */
  public clearColumnFilters(): void {
    this.updateFilterState({ columnFilters: new Map() });
  }

  /**
   * Check if a column has an active filter
   */
  public hasColumnFilter(field: string): boolean {
    return this.getCurrentFilterState().columnFilters.has(field);
  }

  /**
   * Get the number of active column filters
   */
  public getColumnFilterCount(): number {
    return this.getCurrentFilterState().columnFilters.size;
  }

  /**
   * Set the global filter value
   */
  public setGlobalFilter(value: string): void {
    const currentValue = this.getCurrentFilterState().globalFilter;
    if (currentValue !== (value ?? '')) {
      this.updateFilterState({ globalFilter: value ?? '' });
    }
  }

  /**
   * Get the current global filter value
   */
  public getGlobalFilter(): string {
    return this.getCurrentFilterState().globalFilter;
  }

  /**
   * Clear the global filter
   */
  public clearGlobalFilter(): void {
    this.setGlobalFilter('');
  }

  /**
   * Check if global filter is active
   */
  public hasGlobalFilter(): boolean {
    return this.getCurrentFilterState().globalFilter.trim() !== '';
  }

  /**
   * Set a complex filter
   */
  public setComplexFilter(filter: ComplexFilterDescriptor): void {
    if (!isValidFilterExpression(filter)) {
      console.warn('Invalid complex filter provided:', filter);
      return;
    }

    this.updateFilterState({ complexFilter: filter });
  }

  /**
   * Get the current complex filter
   */
  public getComplexFilter(): ComplexFilterDescriptor | undefined {
    return this.getCurrentFilterState().complexFilter;
  }

  /**
   * Clear the complex filter
   */
  public clearComplexFilter(): void {
    this.updateFilterState({ complexFilter: undefined });
  }

  /**
   * Check if complex filter is active
   */
  public hasComplexFilter(): boolean {
    const complexFilter = this.getCurrentFilterState().complexFilter;
    return complexFilter != null && complexFilter.filters.length > 0;
  }

  /**
   * Apply all active filters to the provided data
   */
  public applyFilters<T>(data: ReadonlyArray<T>, gridSettings: GridSettings): T[] {
    if (!data || data.length === 0) {
      return [];
    }

    const filterState = this.getCurrentFilterState();
    let filteredData = [...data];

    // Build combined filter expression
    const filterExpressions: FilterExpression[] = [];

    // Add column filters
    if (filterState.columnFilters.size > 0) {
      const columnFilters = Array.from(filterState.columnFilters.values());
      if (columnFilters.length > 0) {
        filterExpressions.push(columnFilters);
      }
    }

    // Add complex filter
    if (filterState.complexFilter) {
      filterExpressions.push(filterState.complexFilter);
    }

    // Apply combined filters
    if (filterExpressions.length > 0) {
      const combinedFilter = filterExpressions.length === 1 
        ? filterExpressions[0]
        : combineFiltersWithAnd(...filterExpressions);
      
      filteredData = filterBy(filteredData, combinedFilter);
    }

    // Apply global filter
    if (filterState.globalFilter.trim() !== '') {
      filteredData = this.applyGlobalFilterToData(
        filteredData, 
        filterState.globalFilter, 
        gridSettings.globalFilterFields,
      );
    }

    return filteredData;
  }

  /**
   * Apply global filter to data using text search across multiple fields
   */
  private applyGlobalFilterToData<T>(
    data: ReadonlyArray<T>, 
    globalFilter: string, 
    globalFilterFields: ReadonlyArray<string>,
  ): T[] {
    if (!globalFilter.trim() || !globalFilterFields.length) {
      return [...data];
    }

    const textSearchFilter = createTextSearchFilter(
      globalFilterFields, 
      globalFilter.trim(),
    );

    return filterBy(data, textSearchFilter);
  }

  /**
   * Create a date range filter for a specific field
   */
  public createDateRangeFilter(field: string, startDate?: Date, endDate?: Date): ComplexFilterDescriptor {
    return createDateRangeFilter(field, startDate, endDate);
  }

  /**
   * Create a numeric range filter for a specific field
   */
  public createNumericRangeFilter(field: string, min?: number, max?: number): ComplexFilterDescriptor {
    return createNumericRangeFilter(field, min, max);
  }

  /**
   * Create a multi-value filter (OR logic for multiple values)
   */
  public createMultiValueFilter(field: string, values: ReadonlyArray<unknown>, operator = 'eq'): ComplexFilterDescriptor {
    return createMultiValueFilter(field, values, operator);
  }

  /**
   * Create a text search filter across multiple fields
   */
  public createTextSearchFilter(fields: ReadonlyArray<string>, searchTerm: string): ComplexFilterDescriptor {
    return createTextSearchFilter(fields, searchTerm);
  }

  /**
   * Combine current filters with a new filter using AND logic
   */
  public addFilterWithAnd(newFilter: FilterExpression): void {
    const currentState = this.getCurrentFilterState();
    const existingFilters: FilterExpression[] = [];

    // Collect existing filters
    if (currentState.columnFilters.size > 0) {
      existingFilters.push(Array.from(currentState.columnFilters.values()));
    }

    if (currentState.complexFilter) {
      existingFilters.push(currentState.complexFilter);
    }

    // Combine with new filter
    if (existingFilters.length === 0) {
      // If no existing filters, just set the new filter as a complex filter
      this.updateFilterState({
        columnFilters: new Map(),
        complexFilter: Array.isArray(newFilter) ? { logic: 'and', filters: newFilter } : 
                      'logic' in (newFilter as object) ? newFilter as ComplexFilterDescriptor :
                      { logic: 'and', filters: [newFilter as FilterDescriptor] },
      });
    } else {
      const combinedFilter = combineFiltersWithAnd(...existingFilters, newFilter);
      this.updateFilterState({
        columnFilters: new Map(),
        complexFilter: combinedFilter,
      });
    }
  }

  /**
   * Combine current filters with a new filter using OR logic
   */
  public addFilterWithOr(newFilter: FilterExpression): void {
    const currentState = this.getCurrentFilterState();
    const existingFilters: FilterExpression[] = [];

    // Collect existing filters
    if (currentState.columnFilters.size > 0) {
      existingFilters.push(Array.from(currentState.columnFilters.values()));
    }

    if (currentState.complexFilter) {
      existingFilters.push(currentState.complexFilter);
    }

    // Combine with new filter
    if (existingFilters.length === 0) {
      // If no existing filters, just set the new filter as a complex filter
      this.updateFilterState({
        columnFilters: new Map(),
        complexFilter: Array.isArray(newFilter) ? { logic: 'or', filters: newFilter } : 
                      'logic' in (newFilter as object) ? newFilter as ComplexFilterDescriptor :
                      { logic: 'or', filters: [newFilter as FilterDescriptor] },
      });
    } else {
      const combinedFilter = combineFiltersWithOr(...existingFilters, newFilter);
      this.updateFilterState({
        columnFilters: new Map(),
        complexFilter: combinedFilter,
      });
    }
  }

  /**
   * Create a filter descriptor from column configuration
   */
  public createColumnFilter(field: string, value: unknown, column: GridColumn): FilterDescriptor {
    const operator = this.getDefaultOperatorForColumn(column);
    const ignoreCase = column.type !== 'number' && 
                      column.type !== 'date' && 
                      column.type !== 'currency';
    
    return createFieldFilter(field, value, operator, ignoreCase);
  }

  /**
   * Get the default filter operator for a column type
   */
  private getDefaultOperatorForColumn(column: GridColumn): string {
    switch (column.type) {
      case 'number':
      case 'currency':
        return 'eq';
      case 'date':
        return 'dateIs';
      case 'boolean':
        return 'eq';
      case 'text':
      case 'custom':
      default:
        return 'contains';
    }
  }

  /**
   * Create filters from PrimeNG table filter event
   */
  public createFiltersFromPrimeNGEvent(filters: Record<string, unknown>): Map<string, FilterDescriptor> {
    const filterMap = new Map<string, FilterDescriptor>();

    if (!filters) {
      return filterMap;
    }

    Object.keys(filters).forEach((field: string) => {
      const filterMeta = filters[field];
      
      if (filterMeta && 
          typeof filterMeta === 'object' && 
          filterMeta !== null &&
          'value' in filterMeta &&
          filterMeta.value !== null && 
          filterMeta.value !== undefined) {
        
        const matchMode = 'matchMode' in filterMeta ? 
          String(filterMeta.matchMode) : 
          'contains';
        
        const filter = createFieldFilter(
          field, 
          filterMeta.value, 
          matchMode, 
          true,
        );
        filterMap.set(field, filter);
      }
    });

    return filterMap;
  }

  /**
   * Initialize default filter presets
   */
  private initializeDefaultPresets(): void {
    const today = new Date();
    
    // Empty/null value presets
    this.addPreset('empty', 'Empty Values', (field: string) => 
      createFieldFilter(field, '', 'isempty'));
    
    this.addPreset('notEmpty', 'Non-Empty Values', (field: string) => 
      createFieldFilter(field, '', 'isnotempty'));
    
    this.addPreset('null', 'Null Values', (field: string) => 
      createFieldFilter(field, null, 'isnull'));
    
    this.addPreset('notNull', 'Non-Null Values', (field: string) => 
      createFieldFilter(field, null, 'isnotnull'));

    // Date presets
    const startOfToday = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    const endOfToday = new Date(today.getFullYear(), today.getMonth(), today.getDate(), 23, 59, 59);

    this.addPreset('today', 'Today', (field: string) => 
      createDateRangeFilter(field, startOfToday, endOfToday));

    // Week presets
    const startOfWeek = new Date(today);
    startOfWeek.setDate(today.getDate() - today.getDay());
    startOfWeek.setHours(0, 0, 0, 0);

    const endOfWeek = new Date(startOfWeek);
    endOfWeek.setDate(startOfWeek.getDate() + 6);
    endOfWeek.setHours(23, 59, 59, 999);

    this.addPreset('thisWeek', 'This Week', (field: string) => 
      createDateRangeFilter(field, startOfWeek, endOfWeek));

    // Month presets
    const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
    const endOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0, 23, 59, 59);

    this.addPreset('thisMonth', 'This Month', (field: string) => 
      createDateRangeFilter(field, startOfMonth, endOfMonth));
  }

  /**
   * Add a new filter preset
   */
  public addPreset(
    key: string, 
    name: string, 
    filterFactory: (field: string) => FilterExpression,
  ): void {
    if (!key || !name || !filterFactory) {
      console.warn('Invalid preset parameters provided');
      return;
    }

    this.filterPresets.set(key, {
      name,
      description: `Filter for ${name.toLowerCase()}`,
      filterFactory,
    });
  }

  /**
   * Apply a preset filter to a specific field
   */
  public applyPresetFilter(presetKey: string, field: string): void {
    const preset = this.filterPresets.get(presetKey);
    if (!preset) {
      console.warn(`Unknown filter preset: ${presetKey}`);
      return;
    }

    if (!isValidFilterPreset(preset)) {
      console.warn(`Invalid filter preset: ${presetKey}`);
      return;
    }

    try {
      const filter = preset.filterFactory(field);
      if (isValidFilterExpression(filter)) {
        this.setComplexFilter(filter as ComplexFilterDescriptor);
      } else {
        console.warn(`Preset ${presetKey} generated invalid filter`);
      }
    } catch (error) {
      console.error(`Error applying preset filter ${presetKey}:`, error);
    }
  }

  /**
   * Get all available presets
   */
  public getAvailablePresets(): ReadonlyArray<FilterPreset> {
    return Array.from(this.filterPresets.values());
  }

  /**
   * Validate filter values based on column type
   */
  public validateFilterValue(value: unknown, column: GridColumn): boolean {
    if (value === null || value === undefined) {
      return true;
    }

    switch (column.type) {
      case 'number':
      case 'currency':
        return !isNaN(Number(value));
      case 'date':
        return value instanceof Date || !isNaN(Date.parse(String(value)));
      case 'boolean':
        return typeof value === 'boolean' || value === 'true' || value === 'false';
      default:
        return true;
    }
  }

  /**
   * Clear all filters
   */
  public clearAllFilters(): void {
    this.updateFilterState({
      columnFilters: new Map(),
      globalFilter: '',
      complexFilter: undefined,
    });
  }

  /**
   * Get a summary of active filters for display
   */
  public getFilterSummary(): string {
    const state = this.getCurrentFilterState();
    const parts: string[] = [];

    if (state.globalFilter) {
      parts.push(`Global: "${state.globalFilter}"`);
    }

    if (state.columnFilters.size > 0) {
      parts.push(`Column filters: ${state.columnFilters.size}`);
    }

    if (state.complexFilter) {
      const description = describeFilter(state.complexFilter);
      parts.push(`Complex: ${description}`);
    }

    return parts.length > 0 ? parts.join(', ') : 'No filters applied';
  }

  /**
   * Get detailed filter description
   */
  public getDetailedFilterDescription(): string {
    const state = this.getCurrentFilterState();
    const parts: string[] = [];

    if (state.globalFilter) {
      parts.push(`Global search: "${state.globalFilter}"`);
    }

    state.columnFilters.forEach((filter: FilterDescriptor, field: string) => {
      const description = describeFilter(filter);
      parts.push(description);
    });

    if (state.complexFilter) {
      const description = describeFilter(state.complexFilter);
      parts.push(`Complex filter: ${description}`);
    }

    return parts.length > 0 ? parts.join('\n') : 'No filters applied';
  }

  /**
   * Export current filter configuration
   */
  public exportFilterConfig(): Record<string, unknown> {
    const state = this.getCurrentFilterState();
    return {
      columnFilters: Array.from(state.columnFilters.entries()),
      globalFilter: state.globalFilter,
      complexFilter: state.complexFilter,
      exportedAt: new Date().toISOString(),
    };
  }

  /**
   * Import filter configuration
   */
  public importFilterConfig(config: Record<string, unknown>): void {
    if (!config) {
      return;
    }

    const columnFilters = new Map<string, FilterDescriptor>();
    
    if (config.columnFilters && Array.isArray(config.columnFilters)) {
      config.columnFilters.forEach((entry: unknown) => {
        if (Array.isArray(entry) && entry.length === 2) {
          const [key, value] = entry;
          if (typeof key === 'string' && isValidFilterExpression(value)) {
            columnFilters.set(key, value as FilterDescriptor);
          }
        }
      });
    }

    const complexFilter = config.complexFilter && 
                         isValidFilterExpression(config.complexFilter) ? 
                         config.complexFilter as ComplexFilterDescriptor : 
                         undefined;

    this.updateFilterState({
      columnFilters,
      globalFilter: typeof config.globalFilter === 'string' ? config.globalFilter : '',
      complexFilter,
    });
  }

  /**
   * Check if any filters are currently active
   */
  public hasAnyFilters(): boolean {
    const state = this.getCurrentFilterState();
    return this.determineIfFiltersActive(state);
  }

  /**
   * Get total count of active filters
   */
  public getActiveFilterCount(): number {
    const state = this.getCurrentFilterState();
    let count = 0;
    
    if (state.globalFilter.trim() !== '') {
      count++;
    }
    count += state.columnFilters.size;
    if (state.complexFilter && state.complexFilter.filters.length > 0) {
      count++;
    }
    
    return count;
  }

  /**
   * Sync with PrimeNG table filters
   */
  public syncWithPrimeNGTable(filters: Record<string, unknown>, globalFilterValue?: string): void {
    const columnFilters = this.createFiltersFromPrimeNGEvent(filters);
    
    this.updateFilterState({
      columnFilters,
      globalFilter: globalFilterValue ?? '',
    });
  }

  /**
   * Get filters in PrimeNG format
   */
  public toPrimeNGFilters(): Record<string, unknown> {
    const state = this.getCurrentFilterState();
    const primeNGFilters: Record<string, unknown> = {};

    state.columnFilters.forEach((filter: FilterDescriptor, field: string) => {
      primeNGFilters[field] = {
        value: filter.value,
        matchMode: filter.operator,
      };
    });

    return primeNGFilters;
  }
}